'use client';

/**
 * DiscoverySynthesis - AI-generated search synthesis
 *
 * Displays the LLM synthesis from Discovery search results:
 * summary, suggested filters, actions, and followup suggestions.
 *
 * @component_role Discovery Search Insights
 * @business_rule Shows AI synthesis explaining search results.
 *   Provides clickable filter chips and action buttons for refinement.
 *   Displays followup suggestions for multi-turn conversation.
 * @user_outcome Understand results at a glance - see why products match,
 *   refine with suggested filters, or explore followup questions.
 * @integration_point Receives synthesis from Discovery tab data
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { Sparkles, Filter, Zap, MessageCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { DiscoverySynthesis as DiscoverySynthesisType } from '@/lib/api/types';

interface DiscoverySynthesisProps {
  synthesis: DiscoverySynthesisType | null;
  onFilterClick?: (filterKey: string, filterValue: unknown) => void;
  onActionClick?: (action: string) => void;
  onFollowupClick?: (question: string) => void;
}

export function DiscoverySynthesis({
  synthesis,
  onFilterClick,
  onActionClick,
  onFollowupClick,
}: DiscoverySynthesisProps) {
  if (!synthesis) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>Search for products to see AI synthesis</p>
      </div>
    );
  }

  const hasFilters = Object.keys(synthesis.suggested_filters || {}).length > 0;
  const hasActions = synthesis.suggested_actions?.length > 0;
  const hasFollowups = synthesis.followup_suggestions?.length > 0;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Response Type Badge */}
      <ResponseTypeBadge type={synthesis.response_type} />

      {/* Main Synthesis Text */}
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <p className="text-sm leading-relaxed">{synthesis.synthesis}</p>
      </div>

      {/* Suggested Filters */}
      {hasFilters && (
        <Section
          icon={<Filter className="w-4 h-4" />}
          title="Refine your search"
        >
          <div className="flex flex-wrap gap-2">
            {Object.entries(synthesis.suggested_filters).map(([key, value]) => (
              <FilterChip
                key={key}
                label={key}
                value={String(value)}
                onClick={() => onFilterClick?.(key, value)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Suggested Actions */}
      {hasActions && (
        <Section
          icon={<Zap className="w-4 h-4" />}
          title="Quick actions"
        >
          <div className="flex flex-wrap gap-2">
            {synthesis.suggested_actions.map((action, idx) => (
              <ActionChip
                key={idx}
                label={action}
                onClick={() => onActionClick?.(action)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Followup Suggestions */}
      {hasFollowups && (
        <Section
          icon={<MessageCircle className="w-4 h-4" />}
          title="You might also ask"
        >
          <div className="flex flex-col gap-1">
            {synthesis.followup_suggestions.map((question, idx) => (
              <FollowupChip
                key={idx}
                question={question}
                onClick={() => onFollowupClick?.(question)}
              />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function ResponseTypeBadge({ type }: { type: string }) {
  const config = {
    results: { label: 'Results', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
    clarification: { label: 'Clarifying', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    no_results: { label: 'No matches', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    action_confirmation: { label: 'Action', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  }[type] || { label: type, color: 'bg-muted text-muted-foreground border-border' };

  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border w-fit',
      config.color
    )}>
      <Sparkles className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-border pt-3">
      <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
        {icon}
        {title}
      </h4>
      {children}
    </div>
  );
}

function FilterChip({
  label,
  value,
  onClick,
}: {
  label: string;
  value: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs',
        'bg-primary/10 text-primary border border-primary/20',
        'hover:bg-primary/20 transition-colors'
      )}
    >
      <span className="font-medium">{label}:</span>
      <span>{value}</span>
    </button>
  );
}

function ActionChip({
  label,
  onClick,
}: {
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium',
        'bg-accent text-accent-foreground border border-border',
        'hover:bg-accent/80 transition-colors'
      )}
    >
      <Zap className="w-3 h-3" />
      {label}
    </button>
  );
}

function FollowupChip({
  question,
  onClick,
}: {
  question: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'text-left px-3 py-2 rounded-md text-sm',
        'bg-muted/50 hover:bg-muted transition-colors',
        'text-muted-foreground hover:text-foreground'
      )}
    >
      "{question}"
    </button>
  );
}
